import type JobWorkSummaryVM from 'ab-viewModels/workRequest/jobWorkSummary.viewModel';

import { isValidTextInput } from '@acceligentllc/shared/utils/text';
import * as TimeUtils from '@acceligentllc/shared/utils/time';

import type { QuantityUnitType } from '@acceligentllc/shared/enums/quantityUnit';
import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

export const MAX_BILLABLE_WORK_QUANTITY_CHARS = 12;

interface DefinitionField {
	name: Nullable<string>;
	value: Nullable<string>;
	unit: Nullable<string>;
	type: Nullable<string>;
}

interface InformationField {
	name: Nullable<string>;
	values: Nullable<string>;
	units: Nullable<string>;
}

export class FormModel {
	/** Not really used but we need it for comparison when we send it back to BE */
	fieldReportId: Nullable<number>;
	workOrderId: Nullable<number>;
	/* MM-DD-YYYY */
	date: string;
	work: Nullable<string>;
	type: Nullable<string>;
	typeValue: Nullable<string>;
	definitionFields: DefinitionField[];
	informationalFields: InformationField[];
	quantity: Nullable<number>;
	unit: Nullable<string>;
	billingCodeId: Nullable<number>;
	billingCodeUnit: Nullable<QuantityUnitType>;
	customerId: Nullable<string>;
	group: Nullable<string>;
	unitPrice: Nullable<number>;
	description: Nullable<string>;
	revenue: Nullable<number>;
	comment: Nullable<string>;
	workRequestId: Nullable<number>;
	originalUnits: { [key: string]: Nullable<string>; };

	constructor(initial?: JobWorkSummaryVM) {
		this.fieldReportId = initial?.fieldReportId ?? null;
		this.workOrderId = initial?.workOrderId ?? null;
		this.date = initial?.startDate
			? TimeUtils.formatDate(initial.startDate, TimeFormat.DATE_ONLY, TimeFormat.DB_DATE_ONLY)
			: '';
		this.work = initial?.work ?? null;
		this.type = initial?.type ?? null;
		this.typeValue = initial?.typeValue ?? null;
		this.definitionFields = initial ? FormModel.resolveDefinitionFields(initial) : [];
		this.informationalFields = initial ? FormModel.resolveInformationalFields(initial) : [];
		this.quantity = initial?.quantity ?? null;
		this.unit = initial?.unit?.replace('/', '_') ?? null;
		this.billingCodeId = initial?.billingCodeId ?? null;
		this.billingCodeUnit = initial?.billingCode?.unit ?? null;
		this.customerId = initial?.customerId ?? null;
		this.group = initial?.group ?? null;
		this.unitPrice = initial?.unitPrice ?? null;
		this.description = initial?.description ?? null;
		this.revenue = initial?.revenue ?? null;
		this.comment = initial?.comment ?? null;
		this.workRequestId = initial?.workRequestId ?? null;
		this.originalUnits = initial?.originalData ? FormModel.resolveOriginalUnits(initial.originalData) : {};
	}

	static validate = (form: FormModel) => {
		const errors: ValidationErrors = {};

		if (form.billingCodeId === null) {
			errors.billingCodeId = 'Billing Code is required.';
		}

		if (!isValidTextInput(form.work)) {
			errors.work = 'Work is required';
		}

		if (!isValidTextInput(form.type)) {
			errors.type = 'Type is required';
		}

		if (!form.quantity) {
			errors.quantity = 'Quantity is required.';
		} else if (`${form.quantity}`.length > MAX_BILLABLE_WORK_QUANTITY_CHARS) {
			errors.quantity = `Quantity can't be more than ${MAX_BILLABLE_WORK_QUANTITY_CHARS} characters.`;
		}

		if (!form.unit) {
			errors.unit = 'Unit is required.';
		}

		if (form.definitionFields) {
			errors.definitionFields = [];
			for (let i = 0; i < form.definitionFields.length; i++) {
				if (!isValidTextInput(form.definitionFields[i].value)) {
					errors.definitionFields[i] = 'Definition field can\'t be empty.';
				}
			}

		}

		if (form.informationalFields) {
			errors.informationalFields = [];
			for (let i = 0; i < form.informationalFields.length; i++) {
				if (!isValidTextInput(form.informationalFields[i]?.values)) {
					errors.informationalFields[i] = 'Information field can\'t be empty.';
				}
			}
		}

		if (!form.date) {
			errors.date = 'Date is required.';
		}

		if (!form.workRequestId) {
			errors.workRequestId = 'Work Request ID is required';
		}
		return errors;
	};

	static resolveDefinitionFields = (_jws: JobWorkSummaryVM) => {
		const defFields: Nullable<DefinitionField>[] = [
			{ name: _jws.definitionField1Name, value: _jws.definitionField1Value, unit: _jws.definitionField1Unit, type: _jws.definitionField1Type },
			{ name: _jws.definitionField2Name, value: _jws.definitionField2Value, unit: _jws.definitionField2Unit, type: _jws.definitionField2Type },
			{ name: _jws.definitionField3Name, value: _jws.definitionField3Value, unit: _jws.definitionField3Unit, type: _jws.definitionField3Type },
			{ name: _jws.definitionField4Name, value: _jws.definitionField4Value, unit: _jws.definitionField4Unit, type: _jws.definitionField4Type },
		];

		return defFields.filter((_df) => (!!_df?.value || !!_df?.name)) as DefinitionField[];
	};

	static resolveInformationalFields = (_jws: JobWorkSummaryVM) => {
		const infoFields: Nullable<InformationField>[] = [
			{ values: _jws.informationField1Value, name: _jws.informationField1Name, units: _jws.informationField1Unit ? _jws.informationField1Unit : null },
			{ values: _jws.informationField2Value, name: _jws.informationField2Name, units: _jws.informationField2Unit ? _jws.informationField2Unit : null },
			{ values: _jws.informationField3Value, name: _jws.informationField3Name, units: _jws.informationField3Unit ? _jws.informationField3Unit : null },
			{ values: _jws.informationField4Value, name: _jws.informationField4Name, units: _jws.informationField4Unit ? _jws.informationField4Unit : null },
		];

		return infoFields.filter((_if) => (!!_if?.values || !!_if?.name)) as InformationField[];
	};

	static resolveOriginalUnits = (_jws: JobWorkSummaryVM) => {
		const originalUnits: { [key: string]: Nullable<string>; } = {
			d1: _jws.definitionField1Unit, d2: _jws.definitionField2Unit,
			d3: _jws.definitionField3Unit, d4: _jws.definitionField4Unit,
			i1: _jws.informationField1Unit, i2: _jws.informationField2Unit,
			i3: _jws.informationField3Unit, i4: _jws.informationField4Unit,
		};

		return originalUnits;
	};
}
